<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { NInput, NModal } from 'naive-ui'
import type { PaletteItem } from './CommandPalette.types'

const props = defineProps<{
  items: PaletteItem[]
}>()

const open = defineModel<boolean>('open', { required: true })

const query = ref('')
const inputRef = ref<InstanceType<typeof NInput> | null>(null)
const selectedIndex = ref(0)
const listEl = ref<HTMLElement | null>(null)

// Cap at 30 so the modal stays one viewport tall on a small laptop.
// Items are pre-sorted by the parent so the slice keeps the most
// relevant rows at the top (recent threads, pages by recency).
const MAX_RESULTS = 30

// Combobox a11y wiring: the input is a combobox controlling the
// listbox; aria-activedescendant points at the focused option by id so a
// screen reader announces it during arrow-key navigation.
const LISTBOX_ID = 'command-palette-listbox'
function optionId(index: number): string {
  return `palette-option-${index}`
}

const filtered = computed<PaletteItem[]>(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.items.slice(0, MAX_RESULTS)
  // Token AND-match: every whitespace-separated token must appear in
  // either the label or the hint. Cheap and predictable — good enough
  // for a few hundred items; revisit if the page count gets into the
  // thousands and substring scoring stops feeling responsive.
  const tokens = q.split(/\s+/).filter(Boolean)
  return props.items
    .filter((item) => {
      const haystack = `${item.label} ${item.hint ?? ''}`.toLowerCase()
      return tokens.every((t) => haystack.includes(t))
    })
    .slice(0, MAX_RESULTS)
})

const grouped = computed<Array<[string, PaletteItem[]]>>(() => {
  const map = new Map<string, PaletteItem[]>()
  for (const item of filtered.value) {
    const list = map.get(item.group) ?? []
    list.push(item)
    map.set(item.group, list)
  }
  return Array.from(map.entries())
})

watch(open, async (next) => {
  if (next) {
    query.value = ''
    selectedIndex.value = 0
    await nextTick()
    inputRef.value?.focus?.()
  }
})

watch(filtered, () => {
  selectedIndex.value = 0
})

watch(selectedIndex, async (idx) => {
  await nextTick()
  const el = listEl.value?.querySelector<HTMLElement>(`[data-palette-index="${idx}"]`)
  el?.scrollIntoView({ block: 'nearest' })
})

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (filtered.value.length === 0) return
    selectedIndex.value = (selectedIndex.value + 1) % filtered.value.length
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (filtered.value.length === 0) return
    selectedIndex.value = selectedIndex.value === 0 ? filtered.value.length - 1 : selectedIndex.value - 1
  } else if (event.key === 'Enter') {
    event.preventDefault()
    const item = filtered.value[selectedIndex.value]
    if (item) selectAndClose(item)
  } else if (event.key === 'Escape') {
    event.preventDefault()
    open.value = false
  }
}

function selectAndClose(item: PaletteItem): void {
  open.value = false
  // Defer the side effect so the close animation kicks off cleanly
  // before any navigation/drawer-open repaints the screen underneath.
  nextTick(() => item.run())
}

function flatIndex(group: string, indexInGroup: number): number {
  let offset = 0
  for (const [g, list] of grouped.value) {
    if (g === group) return offset + indexInGroup
    offset += list.length
  }
  return offset
}
</script>

<template>
  <NModal
    v-model:show="open"
    transform-origin="center"
    preset="card"
    :bordered="false"
    style="max-width: 640px"
    :close-on-esc="false"
    :auto-focus="false"
    data-testid="command-palette"
  >
    <div class="flex flex-col gap-2" @keydown="onKeydown">
      <NInput
        ref="inputRef"
        v-model:value="query"
        placeholder="Jump to a page, run an action, resume a thread…"
        :input-props="{
          autocomplete: 'off',
          autocapitalize: 'off',
          spellcheck: false,
          role: 'combobox',
          'aria-haspopup': 'listbox',
          'aria-expanded': filtered.length > 0,
          'aria-controls': LISTBOX_ID,
          'aria-activedescendant': filtered.length > 0 ? optionId(selectedIndex) : undefined,
        }"
        data-testid="palette-input"
      />

      <div v-if="filtered.length === 0" class="text-sm text-white/40 px-2 py-6 text-center" data-testid="palette-empty">
        No matches.
      </div>

      <div
        v-else
        :id="LISTBOX_ID"
        ref="listEl"
        class="flex flex-col gap-3 max-h-[60vh] overflow-y-auto"
        role="listbox"
        aria-label="Command palette results"
      >
        <section v-for="[group, list] in grouped" :key="group" class="flex flex-col">
          <h3 class="text-[10px] font-semibold uppercase tracking-widest text-white/30 m-0 px-2 mb-1">
            {{ group }}
          </h3>
          <ul class="list-none p-0 m-0 flex flex-col">
            <li
              v-for="(item, idx) in list"
              :key="item.id"
              :id="optionId(flatIndex(group, idx))"
              :data-palette-index="flatIndex(group, idx)"
              data-testid="palette-item"
              role="option"
              :aria-selected="flatIndex(group, idx) === selectedIndex"
              class="flex items-center justify-between gap-3 px-2 py-1.5 rounded cursor-pointer"
              :class="
                flatIndex(group, idx) === selectedIndex
                  ? 'bg-brand-primary/15 text-white'
                  : 'text-white/80 hover:bg-white/5'
              "
              @mouseenter="selectedIndex = flatIndex(group, idx)"
              @click="selectAndClose(item)"
            >
              <span class="truncate text-sm">{{ item.label }}</span>
              <span v-if="item.hint" class="text-xs text-white/40 truncate shrink-0 max-w-[40%] text-right">
                {{ item.hint }}
              </span>
            </li>
          </ul>
        </section>
      </div>

      <div class="flex items-center justify-between text-[11px] text-white/30 px-1 pt-1 border-t border-white/5">
        <span>↑↓ navigate · ↵ select · esc close</span>
        <span>{{ filtered.length }} {{ filtered.length === 1 ? 'result' : 'results' }}</span>
      </div>
    </div>
  </NModal>
</template>
